/**
 * Security Service for handling 2FA App operations
 */
class Spbc2FAService {
    constructor() {
        this.ajaxUrl = spbcPublic._ajax_url;
        this.nonce = spbcPublic._ajax_nonce;
        this.dialogSelector = '#spbct-2fa-app-qr-code';
        this.codeInputSelector = '#spbct-2fa-app-qr-code input';
        this.qrImageSelector = '#spbct-2fa-app-qr-code-img';
    }

    /**
     * @param {string} action
     * @param {object} additionalData
     * Make AJAX request with proper error handling
     */
    async makeRequest(action, additionalData = {}) {
        const data = {
            security: this.nonce,
            action: action,
            ...additionalData,
        };

        try {
            const response = await jQuery.ajax({
                type: 'POST',
                url: this.ajaxUrl,
                data: data,
            });
            return response;
        } catch (error) {
            console.error(`AJAX request failed for action ${action}:`, error);
            throw new Error('Network or server error occurred');
        }
    }

    /**
     * Get 2FA App QR Code
     */
    async getQrCode() {
        try {
            return await this.makeRequest('spbc_get_2fa_app_qr_code');
        } catch (error) {
            return {
                success: false,
                data: error.message,
            };
        }
    }

    /**
     * Verify 2FA App code
     * @param {string} code
     */
    async verifyCode(code) {
        if (!code || code.trim() === '') {
            throw new Error('Please enter verification code');
        }

        return await this.makeRequest('spbc_check_2fa_app_code', {code: code.trim()});
    }

    /**
     * Disable 2FA App
     * @param {string|null} userId
     */
    async disable2FA(userId = null) {
        return await this.makeRequest('spbc_disable_2fa_app', {'user_id': userId});
    }
}

/**
 * UI Manager for 2FA App interface
 */
class Spbc2FAUIManager {
    constructor(service) {
        this.service = service;
        this.initializeEventListeners();
    }

    initializeEventListeners() {
        jQuery(document).ready(() => {
            this.bindEnableButton();
            this.bindDisableButton();
        });
    }

    bindEnableButton() {
        jQuery(document).on('click', '#spbc-g2fa-enable', async (e) => {
            e.preventDefault();
            await this.showQrCodeDialog();
        });
    }

    bindDisableButton() {
        jQuery(document).on('click', '#spbc-2fa-code-disable', async (e) => {
            e.preventDefault();
            const userId = jQuery(e.target).data('user-id') || null;
            await this.handleDisable2FA(userId);
        });
    }

    async showQrCodeDialog() {
        try {
            const result = await this.service.getQrCode();

            if (!result.success) {
                throw new Error(result.data || 'Failed to generate QR code');
            }

            this.displayQrCode(result.data);
            this.initializeDialog();
        } catch (error) {
            this.showError(error.message);
        }
    }

    displayQrCode(data) {
        const qrContainer = jQuery(this.service.qrImageSelector);
        qrContainer.html(`
            ${data.img}
            <p>Secret Key: <strong>${data.code}</strong></p>
            <p class="description">Enter the 6-digit code from your authenticator app</p>
        `);

        jQuery(this.service.codeInputSelector).val('');
    }

    initializeDialog() {
        jQuery(this.service.dialogSelector).dialog({
            modal: true,
            title: 'Two-Factor Authentication (2FA) Setup',
            buttons: [
                {
                    text: 'Cancel',
                    class: 'button-secondary',
                    click: () => this.closeDialog(),
                },
                {
                    text: 'Verify & Activate',
                    class: 'button-primary',
                    click: () => this.verifyAndActivate(),
                },
            ],
            draggable: false,
            resizable: false,
            width: '400px',
            close: () => this.onDialogClose(),
        });
    }

    async verifyAndActivate() {
        const code = jQuery(this.service.codeInputSelector).val();

        try {
            const result = await this.service.verifyCode(code);

            if (result.success) {
                this.closeDialog();
                this.triggerProfileSubmit();
            } else {
                this.showError(result.data || 'Invalid verification code');
            }
        } catch (error) {
            this.showError(error.message);
        }
    }

    onDialogClose() {
        // Clean up if needed
        jQuery(this.service.codeInputSelector).val('');
    }

    closeDialog() {
        jQuery(this.service.dialogSelector).dialog('close');
    }

    triggerProfileSubmit() {
        const submitButton = jQuery('form#your-profile input#submit');
        if (submitButton.length) {
            submitButton.trigger('click');
        }
    }

    async handleDisable2FA(userId = null) {
        if (!confirm('Are you sure you want to disable Two-Factor Authentication? ' +
            'This will make your account less secure.')) {
            return;
        }

        try {
            const result = await this.service.disable2FA(userId);

            if (result.success) {
                window.location.reload();
            } else {
                this.showError(result.data || 'Failed to disable 2FA');
            }
        } catch (error) {
            this.showError('Something went wrong. Please try again.');
        }
    }

    showError(message) {
        alert(`Error: ${message}`);
    }
}

// Initialize the application
const spbc2FAService = new Spbc2FAService();
new Spbc2FAUIManager(spbc2FAService);
